/** Small helpers for month/week/day math + TZ-aware formatting. */

export type ViewMode = "month" | "week" | "day" | "agenda";
export type TzMode = "workspace" | "local" | "utc";

export const START_OF_WEEK = 1; // Monday

/** Zero-out to start of local day. */
export const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

export const addDays = (d: Date, n: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};

export const startOfMonth = (d: Date): Date => {
  const c = new Date(d.getFullYear(), d.getMonth(), 1);
  return c;
};

export const endOfMonth = (d: Date): Date => {
  const c = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  c.setHours(23, 59, 59, 999);
  return c;
};

export const startOfWeek = (d: Date): Date => {
  const c = startOfDay(d);
  const day = c.getDay();
  const diff = (day - START_OF_WEEK + 7) % 7;
  return addDays(c, -diff);
};

/**
 * Build the 6×7 grid for month view: 42 dates starting from the Monday on
 * or before the first of the month, running forward.
 */
export const buildMonthGrid = (anchor: Date): Date[] => {
  const first = startOfMonth(anchor);
  const start = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

export const buildWeekGrid = (anchor: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i));

export const buildDayHours = (): number[] => Array.from({ length: 24 }, (_, i) => i);

/**
 * ``yyyy-mm-dd`` key derived from a date in the caller's chosen frame.
 * The month grid keys events off local calendar days (whatever TZ the user
 * picked); we compute keys against the display formatter to keep drops
 * accurate.
 */
export const ymdKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Format a UTC ISO in the requested frame. */
export const formatInFrame = (
  iso: string,
  frame: TzMode,
  workspaceTz: string,
  opts: Intl.DateTimeFormatOptions,
): string => {
  const tz = frame === "utc" ? "UTC" : frame === "workspace" ? workspaceTz : undefined;
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(new Date(iso));
};

/** ``yyyy-mm-dd`` for an ISO timestamp interpreted in a frame. */
export const ymdForIso = (iso: string, frame: TzMode, workspaceTz: string): string => {
  const tz = frame === "utc" ? "UTC" : frame === "workspace" ? workspaceTz : undefined;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
};

/** ``HH`` (00-23) in the requested frame. */
export const hourForIso = (iso: string, frame: TzMode, workspaceTz: string): number => {
  const tz = frame === "utc" ? "UTC" : frame === "workspace" ? workspaceTz : undefined;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = parts.find((p) => p.type === "hour")?.value ?? "0";
  return parseInt(h, 10);
};

/** Build UTC ISO for a chosen local date + hour under a given frame. */
export const isoForDropSlot = (
  dateKey: string,
  hour: number,
  frame: TzMode,
  workspaceTz: string,
): string => {
  // Interpret dateKey as being IN the chosen frame, hour local to that frame.
  // We convert by constructing an ISO in the frame's TZ; Date() parses as UTC when Z suffix
  // supplied, but we need the frame's offset. Rather than re-implementing TZ math, we
  // rely on a small trick: format an anchor date in the desired frame to find its offset.
  if (frame === "utc") {
    return new Date(`${dateKey}T${String(hour).padStart(2, "0")}:00:00Z`).toISOString();
  }
  const tz = frame === "workspace" ? workspaceTz : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const naive = new Date(`${dateKey}T${String(hour).padStart(2, "0")}:00:00Z`);
  // Compute offset by asking the browser what the wall-clock reads in `tz`
  // for the naive moment, then correcting.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(naive);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
  const shifted = Date.UTC(
    parseInt(get("year")),
    parseInt(get("month")) - 1,
    parseInt(get("day")),
    parseInt(get("hour")),
    parseInt(get("minute")),
  );
  const offsetMs = shifted - naive.getTime();
  const utcMs = naive.getTime() - offsetMs;
  return new Date(utcMs).toISOString();
};

export const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
